
/** @jsxImportSource react */
import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '../components/ui/Toast';
import { useAuth } from '../contexts/auth'
import { api } from '../services/api'
import { useTranslation } from '../i18n'
import { OrderType, ShippingType } from '../types'
import {
  Package,
  Plane,
  Ship,
  Upload,
  FileText,
  Info,
  Truck,
  Check,
  X,
  ChevronDown,
  CheckCircle,
  MapPin,
  Scaling,
  Weight
} from 'lucide-react'
import { Button } from '../components/ui/Button';

const predefinedWarehouses = [
    { id: 'Dubai', name: 'Dubai' },
    { id: 'China', name: 'China' },
];

const NewShippingRequestPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  
  // Form data state
  const [productName, setProductName] = useState('');
  const [sourceWarehouse, setSourceWarehouse] = useState('');
  const [weight, setWeight] = useState('');
  const [dimensions, setDimensions] = useState('');
  const [shippingType, setShippingType] = useState<ShippingType | ''>('');
  const [notes, setNotes] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // UI state
  const [fileName, setFileName] = useState('')
  const [animateSubmit, setAnimateSubmit] = useState(false)

  // Check if user is verified
  useEffect(() => {
    if (user && user.role === 'CLIENT' && !user.isVerified) {
      toast.error(t('accountNotVerifiedError'));
      navigate('/dashboard')
    }
  }, [user, navigate, t])

  // Order creation mutation
  const orderMutation = useMutation({
    mutationFn: api.createOrder,
    onSuccess: () => {
      setAnimateSubmit(true)
      setTimeout(() => {
        toast.success(t('shippingRequestSuccess'));
        queryClient.invalidateQueries({
          queryKey: ['orders'],
        })
        navigate('/orders')
      }, 1500)
    },
    onError: () => {
      toast.error(t('createOrderError'))
      setIsSubmitting(false);
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('fileTooLargeError', { size: '10MB'}));
        return;
      }
      setImage(file);
      setFileName(file.name);
    }
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!productName.trim() || !sourceWarehouse || !shippingType || !notes.trim() || !image) {
      toast.error(t('fillRequiredFieldsError'));
      return;
    }

    setIsSubmitting(true);
    
    let imageUrl;
    if (image) {
      try {
        imageUrl = await api.uploadFile(image, 'shipping_manifests');
      } catch (err: any) {
        console.error('Upload failed:', err)
        let errorMessage = t('uploadFailedError') || 'File upload failed. Please try again.';
        if (err.code === 'storage/unauthorized') {
            errorMessage = t('storageUnauthorizedError') || "Permission denied. You are not authorized to upload this file.";
        } else if (err.message && err.message.includes('Please wait')) {
            errorMessage = err.message;
        }
        toast.error(errorMessage);
        setIsSubmitting(false);
        return
      }
    }
    
    orderMutation.mutate({
      orderType: OrderType.Shipping,
      productName: productName,
      sourceWarehouseId: sourceWarehouse,
      weight: parseFloat(weight) || undefined,
      dimensions: dimensions || undefined,
      shippingType: shippingType as ShippingType,
      notes: notes,
      productImageUrl: imageUrl,
      quantity: 1,
      trackingNumber: trackingNumber || undefined,
    });
  }, [productName, sourceWarehouse, weight, dimensions, shippingType, notes, trackingNumber, image, orderMutation, t])

  if (animateSubmit) {
    return (
        <div className="flex flex-col items-center justify-center p-10 text-center">
            <CheckCircle className="w-16 h-16 text-primary-500 mb-4 animate-scale-in" />
            <h2 className="text-2xl font-bold text-secondary-800 animate-fade-up">{t('requestSubmitted')}</h2>
            <p className="text-secondary-600 mt-2 animate-fade-up" style={{ animationDelay: '200ms' }}>{t('requestSubmittedDesc')}</p>
        </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-secondary-800">{t('createNewShippingRequest')}</h1>
        <p className="text-secondary-600 mt-2">{t('shippingRequestDescription')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Section 1: Package Details */}
        <div className="p-6 border rounded-xl bg-white shadow-sm">
          <h3 className="text-lg font-semibold text-secondary-800 mb-6 border-b pb-3 flex items-center gap-2">
            <Package className="w-5 h-5 text-primary-500" />
            {t('packageDetails')}
          </h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="productName" className="block text-secondary-700 font-bold mb-2">{t('packageDescription')}*</label>
               <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                    <Package size={18} className="text-secondary-400" />
                </div>
                <input
                    type="text"
                    id="productName"
                    name="productName"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder={t('packageDescriptionPlaceholder')}
                    required
                    className={`w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-secondary-300 ps-10`}
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="sourceWarehouse" className="block text-secondary-700 font-bold mb-2">{t('selectSourceWarehouse')}*</label>
              <div className="relative">
                    <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                        <MapPin size={18} className="text-secondary-400" />
                    </div>
                  <select
                      id="sourceWarehouse"
                      name="sourceWarehouse"
                      value={sourceWarehouse}
                      onChange={(e) => setSourceWarehouse(e.target.value)}
                      required
                      className={`w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-secondary-300 appearance-none bg-white ${!sourceWarehouse ? 'text-secondary-500' : 'text-secondary-900'} ps-10 pe-10`}
                  >
                      <option value="" disabled hidden>{t('selectSourceWarehouse')}</option>
                      {predefinedWarehouses.map((warehouse) => (
                          <option key={warehouse.id} value={warehouse.id} className="text-secondary-900">
                              {warehouse.name}
                          </option>
                      ))}
                  </select>
                  <div className={`pointer-events-none absolute inset-y-0 end-0 flex items-center px-3 text-secondary-700`}>
                      <ChevronDown className="h-4 w-4" />
                  </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="weight" className="block text-secondary-700 font-bold mb-2">{t('weightKgOptional')}</label>
                 <div className="relative">
                    <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                        <Weight size={18} className="text-secondary-400" />
                    </div>
                    <input
                        type="number"
                        id="weight"
                        name="weight"
                        value={weight}
                        onChange={(e) => setWeight(e.target.value)}
                        placeholder={t('weightPlaceholder')}
                        className={`w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-secondary-300 ps-10`}
                    />
                </div>
              </div>
              <div>
                <label htmlFor="trackingNumber" className="block text-secondary-700 font-bold mb-2">
                  {t('trackingNumber')} ({t('optional')})
                </label>
                <div className="relative">
                  <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                    <Truck size={18} className="text-secondary-400" />
                  </div>
                  <input
                    type="text"
                    id="trackingNumber"
                    name="trackingNumber"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder={t('trackingNumberPlaceholder')}
                    className={`w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-secondary-300 ps-10`}
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="dimensions" className="block text-secondary-700 font-bold mb-2">{t('dimensionsCmOptional')}</label>
                 <div className="relative">
                    <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                        <Scaling size={18} className="text-secondary-400" />
                    </div>
                    <input
                        type="text"
                        id="dimensions"
                        name="dimensions"
                        value={dimensions}
                        onChange={(e) => setDimensions(e.target.value)}
                        placeholder={t('dimensionsPlaceholder')}
                        className={`w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-secondary-300 ps-10`}
                    />
                </div>
            </div>
            <div className="p-3 bg-secondary-50 border-s-4 border-secondary-400 text-secondary-800 rounded-e-lg">
                <div className="flex">
                  <div className="py-1">
                    <Info className="h-5 w-5 me-3"/>
                  </div>
                  <div>
                    <p className="text-sm">{t('packageInfoTip')}</p>
                  </div>
                </div>
            </div>
          </div>
        </div>
        
        {/* Section 2: Shipping Method */}
        <div className="p-6 border rounded-xl bg-white shadow-sm">
          <h3 className="text-lg font-semibold text-secondary-800 mb-6 border-b pb-3 flex items-center gap-2">
            <Truck className="w-5 h-5 text-primary-500" />
            {t('shippingMethod')}*
          </h3>
          <div className="space-y-6">
            <button
              type="button"
              onClick={() => setShippingType(ShippingType.Air)}
              className={`w-full p-6 border-2 rounded-xl text-left transition-all duration-300 flex items-center ${
                shippingType === ShippingType.Air
                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                  : 'border-secondary-300 hover:border-primary-400'
              }`}
            >
              <Plane className={`w-8 h-8 me-4 ${shippingType === ShippingType.Air ? 'text-primary-500' : 'text-secondary-500'}`} />
              <div>
                <h3 className="font-bold text-lg text-secondary-800">{t('byAir')}</h3>
                <p className="text-sm text-secondary-600">{t('airFreightDescription')}</p>
                 <div className="mt-2 text-xs font-medium text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full inline-block">{t('faster')}</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setShippingType(ShippingType.Sea)}
              className={`w-full p-6 border-2 rounded-xl text-left transition-all duration-300 flex items-center ${
                shippingType === ShippingType.Sea
                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                  : 'border-secondary-300 hover:border-primary-400'
              }`}
            >
              <Ship className={`w-8 h-8 me-4 ${shippingType === ShippingType.Sea ? 'text-primary-500' : 'text-secondary-500'}`} />
              <div>
                <h3 className="font-bold text-lg text-secondary-800">{t('bySea')}</h3>
                <p className="text-sm text-secondary-600">{t('seaFreightDescription')}</p>
                <div className="mt-2 text-xs font-medium text-secondary-600 bg-secondary-100 px-2 py-0.5 rounded-full inline-block">{t('economical')}</div>
              </div>
            </button>
          </div>
          {shippingType && (
            <p className="text-sm text-primary-600 mt-4 flex items-center">
              <Check className="w-4 h-4 me-1" />
              {t('selected')}: {shippingType === ShippingType.Air ? t('byAir') : t('bySea')}
            </p>
          )}
        </div>

        {/* Section 3: Additional Info */}
        <div className="p-6 border rounded-xl bg-white shadow-sm">
          <h3 className="text-lg font-semibold text-secondary-800 mb-6 border-b pb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary-500" />
            {t('additionalInfo')}
          </h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="notes" className="block text-secondary-700 font-bold mb-2">{t('notesRequired')}*</label>
              <div className="relative">
                <div className={`absolute top-3 start-3 flex items-center pointer-events-none`}>
                    <FileText size={18} className="text-secondary-400" />
                </div>
                <textarea
                    id="notes"
                    name="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t('shippingNotesPlaceholder')}
                    required
                    className={`w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-secondary-300 ps-10`}
                    rows={4}
                ></textarea>
              </div>
            </div>
            <div>
              <label htmlFor="image" className="block text-secondary-700 font-bold mb-2">{t('productListRequired')}*</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed border-secondary-300 rounded-lg bg-secondary-50 hover:bg-secondary-100 transition-colors">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-secondary-400" />
                  <div className="flex text-sm text-secondary-600">
                    <label htmlFor="image" className="relative cursor-pointer rounded-md font-medium text-primary-600 hover:text-primary-500">
                      <span>{t('uploadAFile')}</span>
                      <input 
                        type="file" 
                        id="image" 
                        onChange={handleFileChange} 
                        required 
                        className="sr-only" 
                        accept="image/*,.pdf,.doc,.docx"
                      />
                    </label>
                    <p className="ps-1">{t('orDragAndDrop')}</p>
                  </div>
                  <p className="text-xs text-secondary-500">{t('supportedFormats')}</p>
                  {fileName && (
                    <p className="text-sm text-primary-600 font-medium flex items-center justify-center mt-2">
                      <span className="truncate max-w-xs">{fileName}</span>
                      <button 
                        type="button" 
                        onClick={() => { setImage(null); setFileName(''); }} 
                        className="ms-2 text-secondary-400 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-6 border-t border-secondary-200">
          <Button 
            type="submit" 
            size="lg" 
            disabled={isSubmitting || orderMutation.isPending} 
            isLoading={isSubmitting || orderMutation.isPending}
          >
            {t('submitOrder')}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default NewShippingRequestPage;